"use client";

import { useLiveQuery, useLiveQueryWithStatus } from "@/core/hooks/useLiveQuery";
import { offlineDb, deleteCampagneCascade, setActiveCampagneExclusive } from "@/core/offline/db";
import { deleteWithOfflineFallback } from "@/core/offline/delete-with-fallback";

/**
 * Hook campagne au niveau site (modèle B, refactor sprint 8).
 *
 * - Retourne la campagne ACTIVE du site (`campagne`) si elle existe
 * - Retourne l'historique COMPLET trié par date (`campagnes`)
 * - `ensureCampagne()` : crée une campagne active si aucune n'existe
 * - `closeCampagne()` / `reopenCampagne()` : bascule status
 * - `createNewCampagne()` : démarre une nouvelle campagne en dupliquant les
 *   mesures de la dernière campagne clôturée (fiche identité conservée,
 *   valeurs volatiles remises à zéro). Échoue si une campagne active existe.
 */
export function useCampagneActive(siteId: string) {
  const campagne = useLiveQuery(
    () =>
      offlineDb.campagnes
        .where("siteId")
        .equals(siteId)
        .filter((c) => c.status === "active")
        .first(),
    [siteId]
  );

  const { data: campagnesData, isLoading: campagnesLoading } = useLiveQueryWithStatus(
    () => offlineDb.campagnes.where("siteId").equals(siteId).sortBy("date"),
    [siteId]
  );
  const campagnes = campagnesData ?? [];

  /** Crée une campagne active si aucune n'existe. Atomique. */
  async function ensureCampagne(): Promise<string> {
    let resultId = "";

    await offlineDb.transaction("rw", offlineDb.campagnes, async () => {
      const existing = await offlineDb.campagnes
        .where("siteId")
        .equals(siteId)
        .filter((c) => c.status === "active")
        .first();

      if (existing) {
        resultId = existing.id;
        return;
      }

      const id = crypto.randomUUID();
      const now = new Date();

      await offlineDb.campagnes.add({
        id,
        date: now,
        status: "active",
        siteId,
        createdAt: now,
        updatedAt: now,
        syncStatus: "pending",
      });

      resultId = id;
    });

    return resultId;
  }

  /** Clôture une campagne (status → "cloturee") */
  async function closeCampagne(campagneId: string) {
    await offlineDb.campagnes.update(campagneId, {
      status: "cloturee",
      updatedAt: new Date(),
      syncStatus: "pending",
    });
  }

  /** Réouvre une campagne clôturée (status → "active").
   *  Invariant I14 : clôture d'abord toute autre campagne active du site. */
  async function reopenCampagne(campagneId: string) {
    await setActiveCampagneExclusive(siteId, campagneId);
  }

  /**
   * Démarre une nouvelle campagne sur le site en reprenant la structure
   * (pins, fiche identité des mesures) de la dernière campagne clôturée.
   *
   * Ce qui est conservé dans la mesure dupliquée :
   *   numero, reseau, localisation, reseauType/Complement/Sens,
   *   diametreExt, dn, orientation, materiau, fonteAge, fonteType,
   *   tarification, epaisseurOrigine
   *
   * Ce qui est remis à zéro :
   *   mesureMin, mesureMax, pourcentageCorrosion, codeCouleur, photos, notes
   *
   * Échoue si une campagne active existe déjà (précondition : clôturer d'abord).
   */
  async function createNewCampagne(): Promise<string> {
    let resultId = "";

    await offlineDb.transaction(
      "rw",
      [offlineDb.campagnes, offlineDb.mesures],
      async () => {
        // Vérifier qu'aucune campagne active n'existe
        const active = await offlineDb.campagnes
          .where("siteId")
          .equals(siteId)
          .filter((c) => c.status === "active")
          .first();
        if (active) {
          throw new Error(
            "Une campagne active existe déjà sur ce site. Clôturez-la avant d'en démarrer une nouvelle."
          );
        }

        // Récupérer la dernière campagne clôturée (pour dupliquer ses mesures)
        const closedCampagnes = await offlineDb.campagnes
          .where("siteId")
          .equals(siteId)
          .filter((c) => c.status === "cloturee")
          .sortBy("date");
        const latestClosed = closedCampagnes[closedCampagnes.length - 1];

        // Créer la nouvelle campagne
        const newCampagneId = crypto.randomUUID();
        const now = new Date();
        await offlineDb.campagnes.add({
          id: newCampagneId,
          date: now,
          status: "active",
          siteId,
          createdAt: now,
          updatedAt: now,
          syncStatus: "pending",
        });

        // Dupliquer les mesures de la campagne précédente (si elle existe)
        if (latestClosed) {
          const prevMesures = await offlineDb.mesures
            .where("campagneId")
            .equals(latestClosed.id)
            .toArray();

          for (const m of prevMesures) {
            await offlineDb.mesures.add({
              id: crypto.randomUUID(),
              numero: m.numero,
              // Fiche identité : conservée (réseau structuré + legacy localisation)
              reseau: m.reseau,
              localisation: m.localisation,
              reseauType: m.reseauType ?? null,
              reseauComplement: m.reseauComplement ?? null,
              reseauSens: m.reseauSens ?? null,
              diametreExt: m.diametreExt,
              dn: m.dn,
              orientation: m.orientation,
              materiau: m.materiau,
              fonteAge: m.fonteAge,
              fonteType: m.fonteType,
              tarification: m.tarification,
              epaisseurOrigine: m.epaisseurOrigine,
              // Valeurs volatiles : reset
              mesureMax: null,
              mesureMin: null,
              pourcentageCorrosion: null,
              codeCouleur: null,
              photos: [],
              notes: null,
              // Rattachement
              pinId: m.pinId,
              campagneId: newCampagneId,
              createdAt: now,
              updatedAt: now,
              syncStatus: "pending",
            });
          }
        }

        resultId = newCampagneId;
      }
    );

    return resultId;
  }

  /** Supprime une campagne + ses mesures (offline-first avec sync ultérieure).
   *  Les pins du site ne sont PAS supprimés (ils sont persistants au niveau site). */
  async function deleteCampagne(campagneId: string) {
    await deleteWithOfflineFallback(
      "campagne",
      campagneId,
      `/api/campagnes/${campagneId}`,
      () => deleteCampagneCascade(campagneId)
    );
  }

  return {
    campagne,
    campagnes,
    campagnesLoading,
    ensureCampagne,
    closeCampagne,
    reopenCampagne,
    createNewCampagne,
    deleteCampagne,
  };
}
